/*
 * Peel — Product page (PDP) interactivity.
 * Highlights rail + video lightbox, UGC rail, gallery thumbnail swap,
 * click-to-enlarge on the gallery, colorway (variant) selection, and an
 * addon-aware Add to Cart.
 * Loaded only on the product template. Relies on assets/peel-common.js
 * (peelInitRail, peelInitScrollReveal) being loaded first.
 */
(function () {
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* Held off the elements themselves rather than in an attribute: the theme
     editor hands back markup that has never been bound, and an attribute
     would come back with it and be mistaken for a rail already wired. */
  var wiredUgcRails = new WeakSet();
  /* Same reasoning for the sticky columns: the editor hands back fresh
     columns on every settings change, and re-observing one already wired
     would have it measured twice. */
  var stickyColumns = new WeakSet();

  document.addEventListener('DOMContentLoaded', function () {
    peelInitRail('[data-peel-highlights-rail]', '[data-peel-highlights-prev]', '[data-peel-highlights-next]');
    initUgcRails();
    initHighlightVideos();
    initGallery();
    initGalleryStack();
    initStickyColumns();
    initGalleryZoom();
    initDeviceSwitch();
    initAddToCart();
    initDynamicCheckout();
    initReviewsLink();
    /* Last, and on its own: the reveal is decoration, and the cards are
       readable without it, so a failure here must not take the gallery or
       Add to Cart down with it. */
    initCardReveal();
  });

  /* The theme editor replaces a section's markup on every settings change,
     which throws away the attribute and class the observer put on the cards
     — they would sit at opacity 0 for the rest of the edit session. */
  document.addEventListener('shopify:section:load', function (event) {
    initCardReveal();
    /* Same for the UGC rail: the section that comes back carries new
       buttons and a new rail, and nothing on them is bound yet. */
    initUgcRails(event.target);
    /* And for the sticky columns: toggling the setting is what re-renders
       the section, so the columns that come back are the ones that need
       measuring. */
    initStickyColumns(event.target);
    /* And for the phone dropdown: the section that comes back carries an
       empty hidden control that has never been filled. */
    initDeviceSwitch(event.target);
  });

  /* ---- Sale banner: copy the discount code ----
     Delegated from the document, so a banner the theme editor has just
     re-rendered works without being bound again. The button says
     "Copied" for a moment and the same word goes to a live region, since
     a label change on its own is not announced. Where the clipboard API
     is refused (an insecure origin, an old browser) it falls back to a
     selected textarea and execCommand. */
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-peel-promo-copy]');
    if (!button) return;
    var code = button.getAttribute('data-peel-promo-copy');
    var label = button.querySelector('[data-peel-promo-copy-label]');
    var status = button.parentNode.querySelector('[data-peel-promo-copy-status]');

    function done() {
      var copied = button.getAttribute('data-copied-label') || 'Copied';
      if (label) {
        if (!button.hasAttribute('data-label')) button.setAttribute('data-label', label.textContent);
        label.textContent = copied;
      }
      if (status) status.textContent = copied + ' ' + code;
      button.classList.add('is-copied');
      clearTimeout(button._peelCopyTimer);
      button._peelCopyTimer = setTimeout(function () {
        if (label) label.textContent = button.getAttribute('data-label');
        if (status) status.textContent = '';
        button.classList.remove('is-copied');
      }, 2000);
    }

    function fallback() {
      var field = document.createElement('textarea');
      field.value = code;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      try {
        if (document.execCommand('copy')) done();
      } catch (e) {}
      document.body.removeChild(field);
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, fallback);
    } else {
      fallback();
    }
  });

  /* ---- Fade the cards and the lifestyle rows up as they come into view ----
     The stagger is a per-card transition-delay set in the section markup;
     all this does is stamp the elements so peel-pdp.css and
     peel-shared.css can hide them, and mark each one visible once it is
     on screen. A highlight card that is empty for the product being
     viewed is left out: it only shows in the theme editor, dimmed, and
     revealing it would undo the dimming. The lifestyle band is stamped a
     row at a time rather than as a whole, so a band taller than the
     screen arrives as it is read. A charge row is stamped for the same
     reason and spends it differently: peel-pdp.css fills its bar from
     empty rather than fading the row in. */
  function initCardReveal() {
    if (typeof peelInitScrollReveal !== 'function') return;
    peelInitScrollReveal([
      '.peel-pdp-feature',
      '.peel-highlight-item:not(.peel-highlight-item--empty)',
      '.peel-lifestyle-section .peel-section-head',
      '.peel-lifestyle-item',
      '.peel-charge-row'
    ]);
    dropRailRevealDelays();
  }

  /* The highlights rail is wider than the screen, so the cards past its
     edge are revealed later, when they are swiped to — and the delay that
     sequenced the first arrival would only make those late. Once the rail
     has moved, the cards still to come fade in as they are reached. */
  function dropRailRevealDelays() {
    document.querySelectorAll('[data-peel-highlights-rail]').forEach(function (rail) {
      if (rail.dataset.peelRevealBound) return;
      rail.dataset.peelRevealBound = 'true';
      rail.addEventListener('scroll', function () {
        rail.classList.add('peel-rail-scrolled');
      }, { once: true, passive: true });
    });
  }

  /* ---- UGC rail: arrows page by tile, and a pointer drags it ----
     Every UGC rail on the page is wired on its own, and its arrows are
     looked up inside the section that rail belongs to — a page carrying
     two of them used to leave the second one's arrows dead, and so did
     the theme editor, which hands back one re-rendered section at a time.

     The arrows advance by however many whole tiles are on screen and stop
     at both ends, disabling themselves there rather than wrapping round,
     so the control always says what it can do. Touch swipe is the
     browser's own overflow scrolling, left alone — momentum and snapping
     included; the pointer handlers only extend the same gesture to a
     mouse, which otherwise has nothing but the arrows to go on. */
  function initUgcRails(root) {
    var scopeRoot = root && root.querySelectorAll ? root : document;

    scopeRoot.querySelectorAll('[data-peel-pdp-ugc-rail]').forEach(function (rail) {
      if (wiredUgcRails.has(rail)) return;
      wiredUgcRails.add(rail);

      var scope = rail.closest('.shopify-section') || document;
      var prev = scope.querySelector('[data-peel-pdp-ugc-prev]');
      var next = scope.querySelector('[data-peel-pdp-ugc-next]');

      function metrics() {
        var first = rail.firstElementChild;
        var cs = getComputedStyle(rail);
        var gap = parseFloat(cs.columnGap || cs.gap) || 16;
        var tile = first ? first.getBoundingClientRect().width : 220;
        /* The width the tiles are actually read across: the rail carries
           the page gutter as padding on a phone, and counting that as
           viewable makes a page a tile wider than the view. */
        var viewable = rail.clientWidth
          - (parseFloat(cs.paddingLeft) || 0)
          - (parseFloat(cs.paddingRight) || 0);
        // Whole tiles that fit in view — never less than one.
        var perPage = Math.max(1, Math.floor((viewable + gap) / (tile + gap)));
        return {
          step: (tile + gap) * perPage,
          max: Math.max(0, rail.scrollWidth - rail.clientWidth)
        };
      }

      /* Where the rail would sit with the tile nearest `left` snapped to
         the start of the view. Read off the tiles themselves rather than
         multiplied out from a tile width, because the position a tile
         snaps to is measured from inside the rail's padding — 20px of
         gutter on a phone — and arithmetic from the left edge lands
         a gutter short of it on every tile. */
      function snapped(left) {
        var railBox = rail.getBoundingClientRect();
        var cs = getComputedStyle(rail);
        /* scroll-padding: auto parses to NaN, and auto behaves as 0 here. */
        var inset = parseFloat(cs.scrollPaddingLeft) || 0;
        var origin = railBox.left + (parseFloat(cs.borderLeftWidth) || 0) + inset;
        var best = left;
        var bestGap = Infinity;
        Array.prototype.forEach.call(rail.children, function (tile) {
          var at = rail.scrollLeft + (tile.getBoundingClientRect().left - origin);
          var away = Math.abs(at - left);
          if (away < bestGap) {
            bestGap = away;
            best = at;
          }
        });
        return best;
      }

      function settleAt(left) {
        var max = metrics().max;
        /* The far end is not a tile edge — the last tile sits flush with
           the right of the view — so it is taken as it is. */
        var target = left >= max ? max : Math.max(0, Math.min(max, snapped(left)));
        rail.scrollTo({ left: target, behavior: reducedMotion ? 'auto' : 'smooth' });
      }

      function sync() {
        var max = metrics().max;
        if (prev) prev.disabled = max === 0 || rail.scrollLeft <= 2;
        if (next) next.disabled = max === 0 || rail.scrollLeft >= max - 2;
        /* Only a rail with somewhere to go offers to be dragged. */
        rail.classList.toggle('is-draggable', max > 0);
      }

      function go(dir) {
        var m = metrics();
        if (!m.max) return;
        settleAt(rail.scrollLeft + dir * m.step);
      }

      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });
      rail.addEventListener('scroll', sync, { passive: true });

      /* ---- Drag to swipe ---- */
      var drag = null;
      var swallowClick = false;

      rail.addEventListener('pointerdown', function (event) {
        /* A finger already scrolls the rail, better than this can: it
           carries momentum, and the browser snaps the tile it lands on. */
        if (event.pointerType === 'touch' || event.button !== 0) return;
        if (!metrics().max) return;
        drag = { id: event.pointerId, from: event.clientX, left: rail.scrollLeft, moved: false };
      });

      rail.addEventListener('pointermove', function (event) {
        if (!drag || event.pointerId !== drag.id) return;
        var travelled = event.clientX - drag.from;

        if (!drag.moved) {
          /* A click with a tremor in it is still a click, not a swipe. */
          if (Math.abs(travelled) < 4) return;
          drag.moved = true;
          rail.classList.add('is-dragging');
          /* The stylesheet scrolls this rail smoothly and snaps it to a
             tile. Both fight a pointer holding it: one animates towards
             every position the drag passes through, so the rail trails
             the cursor, and the other pulls it back to a tile mid-drag.
             They are handed back at release. */
          rail.style.scrollBehavior = 'auto';
          rail.style.scrollSnapType = 'none';
          /* Keeps the drag alive past the edge of the rail, and past the
             images inside it, which would otherwise take the pointer. */
          if (rail.setPointerCapture) rail.setPointerCapture(event.pointerId);
        }

        event.preventDefault();
        rail.scrollLeft = drag.left - travelled;
      });

      function release(event) {
        if (!drag || (event && event.pointerId !== drag.id)) return;
        var moved = drag.moved;
        drag = null;
        if (!moved) return;

        rail.classList.remove('is-dragging');
        rail.style.scrollBehavior = '';
        rail.style.scrollSnapType = '';
        /* Snapping is on again, but the browser only applies it to
           scrolls of its own — the rail was left wherever the pointer
           dropped it, so it is walked to the nearest tile from here. */
        settleAt(rail.scrollLeft);

        /* The pointerup that ends a drag is followed by a click on
           whatever the swipe finished over, and a UGC tile can be a link. */
        swallowClick = true;
        window.setTimeout(function () { swallowClick = false; }, 0);
      }

      rail.addEventListener('pointerup', release);
      rail.addEventListener('pointercancel', release);
      rail.addEventListener('click', function (event) {
        if (!swallowClick) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
      /* Native image dragging would hijack the gesture a tile in. */
      rail.addEventListener('dragstart', function (event) {
        if (drag) event.preventDefault();
      });

      /* A window resize is not the only thing that changes what fits: the
         tiles are resized at the 640px breakpoint, by a web font landing,
         and by the theme editor re-flowing the section around them. The
         observer catches all of those, and unlike a window listener it is
         released with the rail when the editor replaces the section. */
      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(sync).observe(rail);
      } else {
        window.addEventListener('resize', sync);
      }

      // Images landing late change scrollWidth; re-check when they do.
      rail.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', sync, { once: true });
      });

      sync();
    });
  }

  /* ---- Highlights video lightbox ---- */
  function initHighlightVideos() {
    var triggers = document.querySelectorAll('[data-peel-video-trigger]');
    if (!triggers.length) return;

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var dialog = document.querySelector('[data-peel-video-dialog="' + trigger.dataset.videoBlockId + '"]');
        if (!dialog) return;
        dialog.showModal();
        var video = dialog.querySelector('video');
        if (video) video.play();
      });
    });

    document.querySelectorAll('.peel-highlight-dialog').forEach(function (dialog) {
      var closeBtn = dialog.querySelector('[data-index="hl-close"]');
      if (closeBtn) closeBtn.addEventListener('click', function () { dialog.close(); });

      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) dialog.close();
      });

      dialog.addEventListener('close', function () {
        var video = dialog.querySelector('video');
        if (video) video.pause();
      });
    });
  }

  /* ---- Gallery — one colourway at a time ----
     Every slide, thumbnail, dot and tile carries the colourway it is a
     shot of on data-peel-color, empty when it is a shot of no colourway
     in particular. That one attribute is the whole of the matching: the
     section works out which media belongs to which colour once, in
     Liquid, and nothing here re-derives it from alt text.

     Dealing is the same three-way split everywhere it is used. This
     colour's own media leads, the media that belongs to every colour
     follows it, and the rest leaves the gallery — hidden, uncounted, and
     still holding its source on data-src, so a colourway's images are
     fetched the first time that colourway is looked at and never before. */
  function mediaColor(el) {
    return (el.getAttribute('data-peel-color') || '').trim();
  }

  function dealMedia(items, colorName) {
    var wanted = String(colorName || '').trim();
    var mine = [];
    var shared = [];
    var others = [];
    items.forEach(function (el) {
      var color = mediaColor(el);
      if (!color) shared.push(el);
      else if (wanted && color === wanted) mine.push(el);
      else others.push(el);
    });
    /* A colourway with no shot of its own — an unlabelled catalogue, or
       one colour the photographer never got to — would otherwise be left
       with the lifestyle frames and nothing of the product. Nothing to
       scope by is a reason to show everything, and it is decided per
       colourway, so the colours that are covered still get their own
       gallery. The section renders with the same fallback. */
    if (!mine.length) return { included: items.slice(), excluded: [] };
    return { included: mine.concat(shared), excluded: others };
  }

  /* Media dealt out of the gallery is printed without a src, so showing
     it is also the moment it is first fetched. One assignment, once. */
  function hydrate(el) {
    var img = el.tagName === 'IMG' ? el : el.querySelector('img');
    if (!img || !img.getAttribute('data-src')) return;
    img.src = img.getAttribute('data-src');
    img.removeAttribute('data-src');
  }

  /* The merchant's sequence, tagged once on load. The section renders in
     dealt order rather than published order — this colourway's shots
     first — so a re-deal has to sort back to something stable instead of
     reading whatever the last deal left behind. */
  function tagOrder(items) {
    Array.prototype.forEach.call(items, function (el, i) {
      if (el.dataset.peelOrder === undefined) el.dataset.peelOrder = i;
    });
  }

  function inOrder(items) {
    return Array.prototype.slice.call(items).sort(function (a, b) {
      return a.dataset.peelOrder - b.dataset.peelOrder;
    });
  }

  /* Which colourway the gallery is currently dealt for. Rendered onto the
     element by the section and rewritten on every change, so the reveal
     control and the colourway sync deal from the same answer. */
  function galleryColor(gallery) {
    return gallery.getAttribute('data-peel-pdp-color') || '';
  }

  /* Re-parents one dealt list: included in order and hydrated, excluded
     after them and hidden. Returns how many are on screen. */
  function dealInto(parent, items, colorName) {
    var deal = dealMedia(inOrder(items), colorName);
    deal.included.forEach(function (el) {
      parent.appendChild(el);
      el.classList.remove('is-hidden');
      hydrate(el);
    });
    deal.excluded.forEach(function (el) {
      parent.appendChild(el);
      el.classList.add('is-hidden');
    });
    return deal.included.length;
  }

  /* ---- Gallery — carousel ----
     Every image is a slide in a horizontal snap track. Thumbnails, the
     colourway sync and a swipe on the phone all move the same track, and
     the track's scroll position is the single source of truth for which
     image is showing — so the thumbnail, the dots and the counter cannot
     disagree with each other. Only the slides for the selected colourway
     are in the track, so the counter reads "3 of 6", not "3 of 48". */
  function visibleParts(gallery, selector) {
    return Array.prototype.slice.call(
      gallery.querySelectorAll(selector + ':not(.is-hidden)')
    );
  }

  function galleryIndex(gallery, index) {
    var slides = visibleParts(gallery, '[data-peel-pdp-slide]');
    if (!slides.length) return;
    if (index < 0) index = 0;
    if (index > slides.length - 1) index = slides.length - 1;

    visibleParts(gallery, '[data-peel-pdp-thumb]').forEach(function (t, i) {
      t.classList.toggle('is-active', i === index);
    });
    visibleParts(gallery, '[data-peel-pdp-dot]').forEach(function (d, i) {
      d.classList.toggle('is-active', i === index);
    });
    var count = gallery.querySelector('[data-peel-pdp-count]');
    if (count) count.textContent = (index + 1) + '/' + slides.length;
  }

  function galleryGoTo(gallery, index) {
    var track = gallery.querySelector('[data-peel-pdp-track]');
    if (!track) return;
    /* Slides are exactly one track wide, so the offset is the index. */
    track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
    galleryIndex(gallery, index);
  }

  function initGallery() {
    document.querySelectorAll('.peel-pdp-gallery').forEach(function (gallery) {
      var track = gallery.querySelector('[data-peel-pdp-track]');
      if (!track) return;

      tagOrder(gallery.querySelectorAll('[data-peel-pdp-slide]'));
      tagOrder(gallery.querySelectorAll('[data-peel-pdp-thumb]'));
      tagOrder(gallery.querySelectorAll('[data-peel-pdp-dot]'));

      Array.prototype.forEach.call(gallery.querySelectorAll('[data-peel-pdp-thumb]'), function (thumb) {
        /* Read at click time, not bound at init: a colourway change
           re-orders the strip, so the index a thumbnail had on load is
           not the one it has now. */
        thumb.addEventListener('click', function () {
          var index = visibleParts(gallery, '[data-peel-pdp-thumb]').indexOf(thumb);
          if (index > -1) galleryGoTo(gallery, index);
        });
      });

      /* Coalesced to one read per frame: a swipe fires scroll far more
         often than the dots need repainting. */
      var pending = false;
      track.addEventListener('scroll', function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          var w = track.clientWidth || 1;
          galleryIndex(gallery, Math.round(track.scrollLeft / w));
        });
      }, { passive: true });
    });
  }

  /* Re-deals the track, the thumbnail strip and the dots to the gallery's
     current colourway and returns to that colourway's first shot. The
     counter, the strip and the dots go away entirely when a colourway has
     only one image to show, rather than counting to one. */
  function renderCarousel(gallery) {
    var track = gallery.querySelector('[data-peel-pdp-track]');
    if (!track) return;
    var colorName = galleryColor(gallery);

    var visible = dealInto(track, gallery.querySelectorAll('[data-peel-pdp-slide]'), colorName);
    var thumbs = gallery.querySelector('.peel-pdp-thumbs');
    if (thumbs) {
      dealInto(thumbs, gallery.querySelectorAll('[data-peel-pdp-thumb]'), colorName);
      thumbs.hidden = visible < 2;
    }
    var dots = gallery.querySelector('.peel-pdp-gallery-dots');
    if (dots) {
      dealInto(dots, gallery.querySelectorAll('[data-peel-pdp-dot]'), colorName);
      dots.hidden = visible < 2;
    }
    var count = gallery.querySelector('[data-peel-pdp-count]');
    if (count) count.hidden = visible < 2;

    /* Straight to the front rather than gliding there: the track holds a
       different set of images now, not a different position within the
       set already showing. The behaviour has to be named, because the
       track's own CSS asks for smooth by default. */
    track.scrollTo({ left: 0, behavior: 'instant' });
    galleryIndex(gallery, 0);
  }

  /* ---- Main layout — sticky columns ----
     CSS pins both columns and the grid picks which one moves (see
     peel-pdp.css); this decides where the moving one rests. A column that
     fits on screen is simply pinned under the header. One taller than the
     screen cannot be — a top pin would hide its end for the whole scroll —
     so it scrolls with the page until an edge comes into view and rests
     there: its bottom edge on the way down, its top edge on the way up.

     The browser does all of the moving. Script only acts when the scroll
     turns round, or a column changes height, and then it does one thing:
     it takes where the column is on screen right now and re-anchors it
     there, as a margin inside its row (so it scrolls natively from that
     spot) plus the pin for the new direction. Nothing is set per frame,
     so there is nothing to lag behind the scroll.

     Re-anchoring in place is also what keeps an accordion still. A pin
     worked out from the column's height alone would have opening
     "Shipping & returns" on a column pinned by its bottom lift the whole
     column by the height of the answer, sliding the row the shopper had
     just clicked out from under the pointer. Now a change of height keeps the
     column where it was, and the new text simply runs on below it.
     ResizeObserver reports before paint, so the frame the shopper sees is
     already the corrected one.

     The one case a pin cannot hold is a column resting against the end of
     the section, which has nowhere left to grow but up. There the page is
     scrolled by the difference instead, which leaves the column still on
     screen and moves the rest of the page around it — the same trade the
     browser's own scroll anchoring makes. */
  var STICKY_TOP = 96;
  var STICKY_BOTTOM_GAP = 24;
  var stickyDesktop = window.matchMedia('(min-width: 901px)');
  var stickyState = new WeakMap();
  var stickyHeading = 'down';
  var stickyScrollY = window.scrollY;
  var stickyObserver = window.ResizeObserver ? new ResizeObserver(onStickyResize) : null;

  function initStickyColumns(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-peel-pdp-sticky]').forEach(function (column) {
      if (stickyColumns.has(column)) return;
      stickyColumns.add(column);
      pinSticky(column, column.getBoundingClientRect().top);
      /* Catches the accordions, "See more images", the colourway
         re-deal and images arriving late, all of which change a column's
         height without anything here having to know they happened. The
         pin itself never resizes a column, so the callback cannot feed
         itself. */
      if (stickyObserver) stickyObserver.observe(column);
    });
  }

  /* Where the grid row starts on screen, and how tall the other column
     is — the row is as tall as the taller of the two, so that is the
     room this one has to move in. Measured bare, without any shift the
     other column may still be carrying: a column that has just become
     the taller one is about to lose its shift, not keep it. */
  function stickyRow(column) {
    var layout = column.parentElement;
    var other = 0;
    Array.prototype.forEach.call(layout.children, function (child) {
      if (child !== column) other = Math.max(other, child.offsetHeight);
    });
    return {
      top: layout.getBoundingClientRect().top + (parseFloat(getComputedStyle(layout).paddingTop) || 0),
      other: other
    };
  }

  /* Anchors the column at screen position y for the current direction.
     The margin puts its natural place in the row right there, so from
     here on it scrolls with the page; the pin is the edge at which it
     stops. The margin sits on the edge the column is moving away from:
     a sticky box keeps its margin inside the row too, so a margin on the
     leading edge would hold it short of the pin (see peel-pdp.css). */
  function pinSticky(column, y) {
    var row = stickyRow(column);
    var height = column.offsetHeight;
    var room = Math.max(0, row.other - height);
    var state = {
      heading: stickyHeading,
      height: height,
      rowHeight: Math.max(row.other, height)
    };
    if (state.heading === 'down') {
      state.shift = Math.min(Math.max(y - row.top, 0), room);
      /* Stop with the bottom edge on screen, or under the header if the
         whole column fits — and never lower than it is now, or a column
         that has just got shorter would drop to meet the new edge. */
      state.pin = Math.min(STICKY_TOP, window.innerHeight - height - STICKY_BOTTOM_GAP, y);
      column.style.setProperty('--peel-sticky-align', 'start');
      column.style.setProperty('--peel-sticky-shift-top', state.shift + 'px');
      column.style.setProperty('--peel-sticky-shift-bottom', '0px');
      column.style.setProperty('--peel-sticky-top', state.pin + 'px');
      column.style.setProperty('--peel-sticky-bottom', 'auto');
    } else {
      state.shift = Math.min(Math.max(row.top + state.rowHeight - y - height, 0), room);
      /* Stop with the top edge under the header — and, as above, never
         higher than it is now. Expressed as a bottom offset because a top
         offset only ever holds a box down, and on the way up the column
         has to be held back from following the page. */
      state.pin = Math.max(STICKY_TOP, y);
      column.style.setProperty('--peel-sticky-align', 'end');
      column.style.setProperty('--peel-sticky-shift-top', '0px');
      column.style.setProperty('--peel-sticky-shift-bottom', state.shift + 'px');
      column.style.setProperty('--peel-sticky-top', 'auto');
      column.style.setProperty('--peel-sticky-bottom', window.innerHeight - state.pin - height + 'px');
    }
    stickyState.set(column, state);
  }

  /* Where the column was on screen before its height changed, worked out
     from its last anchor rather than read off the page: by the time the
     observer reports, a column anchored by its bottom or pinned by its
     bottom edge has already been moved. */
  function stickyWasAt(state, rowTop) {
    var lowest = rowTop + state.rowHeight - state.height;
    var y = state.heading === 'down'
      ? Math.max(rowTop + state.shift, state.pin)
      : Math.min(lowest - state.shift, state.pin);
    return Math.min(Math.max(y, rowTop), lowest);
  }

  function onStickyResize(entries) {
    if (!stickyDesktop.matches) return;
    var layouts = [];
    entries.forEach(function (entry) {
      var column = entry.target;
      if (!column.isConnected) {
        stickyObserver.unobserve(column);
        return;
      }
      if (layouts.indexOf(column.parentElement) === -1) layouts.push(column.parentElement);
    });
    layouts.forEach(function (layout) {
      var columns = Array.prototype.filter.call(layout.children, function (child) {
        return child.hasAttribute('data-peel-pdp-sticky');
      });
      /* Every "before" is taken ahead of any write: re-anchoring one
         column changes the room the other has. */
      var rowTop = stickyRow(columns[0]).top;
      var before = columns.map(function (column) {
        var state = stickyState.get(column);
        return state ? stickyWasAt(state, rowTop) : column.getBoundingClientRect().top;
      });
      var resized = columns.map(function (column) {
        var state = stickyState.get(column);
        return !state || state.height !== column.offsetHeight;
      });
      columns.forEach(function (column, i) {
        pinSticky(column, before[i]);
      });
      /* Only for a column that itself changed: a gallery loading its
         images late must not scroll the page out from under the buy
         column. */
      for (var i = 0; i < columns.length; i++) {
        if (!resized[i]) continue;
        var drift = columns[i].getBoundingClientRect().top - before[i];
        if (Math.abs(drift) >= 1) {
          window.scrollBy({ top: drift, behavior: 'instant' });
          stickyScrollY = window.scrollY;
          break;
        }
      }
    });
  }

  function eachSticky(fn) {
    if (!stickyDesktop.matches) return;
    document.querySelectorAll('[data-peel-pdp-sticky]').forEach(fn);
  }

  /* One listener for the window rather than one per column: the editor
     replaces columns often enough that per-column listeners would pile up
     on elements no longer in the page. Scroll acts only on a change of
     direction, so most events return after one comparison. */
  window.addEventListener('scroll', function () {
    var y = window.scrollY;
    var heading = y > stickyScrollY ? 'down' : y < stickyScrollY ? 'up' : stickyHeading;
    stickyScrollY = y;
    if (heading === stickyHeading) return;
    stickyHeading = heading;
    eachSticky(function (column) {
      pinSticky(column, column.getBoundingClientRect().top);
    });
  }, { passive: true });

  /* A resize changes the screen every pin is measured against. Coalesced
     to a frame — a drag fires resize far more often than that needs. */
  var stickyPending = false;
  window.addEventListener('resize', function () {
    if (stickyPending) return;
    stickyPending = true;
    requestAnimationFrame(function () {
      stickyPending = false;
      eachSticky(function (column) {
        pinSticky(column, column.getBoundingClientRect().top);
      });
    });
  });

  /* ---- Gallery — stacked grid ----
     Every tile of the current colourway is shown; the colourway re-deal
     decides which tiles those are. */
  function initGalleryStack() {
    document.querySelectorAll('[data-peel-pdp-stack]').forEach(function (stack) {
      tagOrder(stack.querySelectorAll('[data-peel-pdp-tile]'));
    });
  }

  function stackTiles(stack) {
    return Array.prototype.slice.call(stack.querySelectorAll('[data-peel-pdp-tile]'));
  }

  /* The grid for the gallery's current colourway: that colour's tiles,
     then the ones belonging to no colour, and the rest excluded. */
  function dealStack(gallery) {
    var stack = gallery.querySelector('[data-peel-pdp-stack]');
    if (!stack) return null;
    return dealMedia(inOrder(stackTiles(stack)), galleryColor(gallery));
  }

  function renderStack(gallery, deal) {
    var stack = gallery.querySelector('[data-peel-pdp-stack]');
    if (!stack || !deal || !deal.included.length) return;
    var ordered = deal.included;

    var badge = stack.querySelector('.peel-badge');
    ordered.forEach(function (tile, i) {
      stack.appendChild(tile);
      tile.classList.toggle('peel-pdp-tile--lead', i === 0);
      tile.classList.remove('is-hidden');
      hydrate(tile);
    });
    deal.excluded.forEach(function (tile) {
      stack.appendChild(tile);
      tile.classList.remove('peel-pdp-tile--lead');
      tile.classList.add('is-hidden');
    });
    /* The "New" flag belongs to the product, not to whichever image
       happened to lead when the page was rendered. */
    if (badge) ordered[0].appendChild(badge);
  }

  /* ---- Gallery — enlarged image ----
     Clicking a shot opens it at full size in the gallery's viewer, and the
     arrows walk the shots either side of it. Which shots those are is read
     at open time, not bound at init: the gallery is re-dealt on every
     colourway change and the stack changes with it, so
     the set on screen when the viewer opens is the only set worth paging
     through. That also keeps the counter honest — "2 of 6" of what the
     shopper can see, not of the catalogue behind it. */
  function zoomShots(gallery) {
    return Array.prototype.slice.call(gallery.querySelectorAll('[data-peel-pdp-zoom]'))
      .filter(function (btn) {
        /* The colourway deal hides the image, not the button sitting on
           it. */
        var holder = btn.closest('.peel-pdp-slide, .peel-pdp-tile');
        return holder && !holder.classList.contains('is-hidden');
      });
  }

  function initGalleryZoom() {
    document.querySelectorAll('.peel-pdp-gallery').forEach(function (gallery) {
      /* The viewer is the section's, not the gallery's: it is kept out of
         the sticky gallery's stacking context — see the note beside it in
         sections/peel-product-main.liquid. */
      var section = gallery.closest('[data-peel-pdp-section-id]');
      var dialog = section && section.querySelector('[data-peel-pdp-zoom-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      var img = dialog.querySelector('[data-peel-pdp-zoom-img]');
      var count = dialog.querySelector('[data-peel-pdp-zoom-count]');
      var prev = dialog.querySelector('[data-peel-pdp-zoom-prev]');
      var next = dialog.querySelector('[data-peel-pdp-zoom-next]');
      if (!img) return;
      var shots = [];
      var at = 0;

      function show(index) {
        if (!shots.length) return;
        /* Wraps, so the arrows never dead-end on the first or last shot. */
        at = (index + shots.length) % shots.length;
        var shot = shots[at];
        /* srcset first: set the other way round the browser starts on the
           src and then picks again once the candidates arrive. */
        img.srcset = shot.dataset.zoomSrcset || '';
        img.src = shot.dataset.zoomSrc || '';
        img.alt = shot.dataset.zoomAlt || '';
        if (count) count.textContent = (at + 1) + '/' + shots.length;
        var alone = shots.length < 2;
        if (prev) prev.hidden = alone;
        if (next) next.hidden = alone;
        if (count) count.hidden = alone;
      }

      Array.prototype.forEach.call(gallery.querySelectorAll('[data-peel-pdp-zoom]'), function (shot) {
        shot.addEventListener('click', function () {
          shots = zoomShots(gallery);
          var index = shots.indexOf(shot);
          if (index < 0) return;
          show(index);
          dialog.showModal();
        });
      });

      if (prev) prev.addEventListener('click', function () { show(at - 1); });
      if (next) next.addEventListener('click', function () { show(at + 1); });

      var closeBtn = dialog.querySelector('[data-peel-pdp-zoom-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () { dialog.close(); });

      /* Anywhere off the image — the dialog fills the screen, so that is
         most of it. Escape is the dialog's own. */
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) dialog.close();
      });

      dialog.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); show(at - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); show(at + 1); }
      });

      /* The carousel is left on the shot the viewer was closed at rather
         than the one it was opened from: the shopper paged to it, so it is
         the one they are looking at. The stack has no such position. */
      dialog.addEventListener('close', function () {
        if (gallery.querySelector('[data-peel-pdp-track]')) galleryGoTo(gallery, at);
      });
    });
  }

  /* ---- The phone the case is cut for ----
     A case line is one product per phone, so the phone is not an option
     on this product — it is which product the shopper is on. The
     dropdown says so, and choosing another phone goes to that phone's
     own page.

     The entries are rendered by snippets/peel-pdp-device-switch.liquid,
     one per phone the line is cut for, each carrying where that phone's
     case is and which colourway is which variant of it. The control
     itself is rendered empty and hidden: the phones are ordered by
     peel-device-map.js rather than by the order the collection is sorted
     in, and a dropdown that navigates is no use to a page with no
     script.

     Held in a WeakSet rather than marked on the element, for the reason
     the rails are: the theme editor hands back markup that has never
     been bound, and an attribute would come back with it and be mistaken
     for a control already wired. */
  var wiredDeviceSwitches = new WeakSet();

  function deviceEntries(box) {
    var node = box.querySelector('[data-peel-pdp-devices]');
    if (!node) return [];
    try {
      var parsed = JSON.parse(node.textContent);
      return Object.prototype.toString.call(parsed) === '[object Array]' ? parsed : [];
    } catch (error) {
      /* A line whose JSON did not survive the page keeps its dropdown
         hidden, which leaves the buy column exactly as it renders. */
      return [];
    }
  }

  /* Two URLs for the same product page. Compared on the `/products/…`
     tail rather than whole: the page may have arrived with a `?variant=`
     on it, through a collection, or on one of the store's locale roots,
     and none of those make it a different product from the one the
     entries name. */
  function productKey(url) {
    var path = String(url || '').split('?')[0].replace(/\/$/, '');
    var at = path.indexOf('/products/');
    return at > -1 ? path.slice(at) : path;
  }

  function samePage(a, b) {
    return productKey(a) === productKey(b);
  }

  /* The colourway the shopper is looking at, to carry across to the
     phone they pick. The checked swatch first — it is the control they
     set — then the name beside the label, which is what a product with
     one colourway and no row to press shows. */
  function currentColorway(section) {
    if (!section) return '';
    var checked = section.querySelector('.peel-pdp-swatch-btn input:checked');
    if (checked && checked.value) return checked.value;
    var name = section.querySelector('[data-peel-pdp-colorway-name]');
    return name ? name.textContent.trim() : '';
  }

  /* Where choosing this phone goes. The colour comes along wherever that
     phone is sold in it and has it in stock; where it is not, the plain
     product page, which opens on its own first colourway rather than on
     a sold-out one the shopper never asked for. */
  function deviceHref(device, colorway) {
    var url = device.url;
    if (!url || !colorway) return url;
    var wanted = colorway.toLowerCase();
    var match = null;
    (device.variants || []).forEach(function (variant) {
      if (match || !variant || !variant.color) return;
      if (variant.color.toLowerCase() === wanted && variant.available !== false) match = variant;
    });
    if (!match) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'variant=' + match.id;
  }

  function initDeviceSwitch(root) {
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-peel-pdp-device]'), function (box) {
      if (wiredDeviceSwitches.has(box)) return;
      wiredDeviceSwitches.add(box);

      var select = box.querySelector('[data-peel-pdp-device-select]');
      if (!select) return;
      var section = box.closest('[data-peel-pdp-section-id]');
      var devices = deviceEntries(box);
      var byKey = {};
      var count = 0;

      var groups = (window.peelDeviceMap && window.peelDeviceMap.group)
        ? window.peelDeviceMap.group(devices)
        : (devices.length ? [{ name: '', devices: devices }] : []);

      /* The brand headings are what tells an iPhone apart from a Galaxy
         in a list of both. A page lists its own product's brand, so
         there is usually only one, and a heading reading "Apple" over
         every option in a list of iPhones is a line of chrome saying
         what the page already says. Counted across the groups rather
         than assumed from the one brand asked for: a phone the
         catalogue could not place comes back in a group of its own. */
      var brands = {};
      var brandCount = 0;
      devices.forEach(function (device) {
        var brand = device && device.brand;
        if (!brand || brands[brand]) return;
        brands[brand] = true;
        brandCount += 1;
      });

      groups.forEach(function (group) {
        var parent = select;
        if (group.name && brandCount > 1) {
          parent = document.createElement('optgroup');
          parent.label = group.name;
          select.appendChild(parent);
        }
        group.devices.forEach(function (device) {
          var option = document.createElement('option');
          option.value = device.key;
          option.textContent = device.label || device.value || device.key;
          /* Said in the option rather than left to the product page to
             break the news, so a shopper does not pick a phone, wait for
             a page and find it sold out. */
          if (device.available === false) option.textContent += ' — sold out';
          parent.appendChild(option);
          byKey[device.key] = device;
          count += 1;
        });
      });

      /* Nothing to switch between: no phones at all, or every phone in
         the list is this page. A line cut for one phone so far, or one
         whose other products declare no compatibility, leaves a control
         whose only choice is the page it sits on — worse than no
         control, since it reads as a way out that goes nowhere. */
      var elsewhere = 0;
      Object.keys(byKey).forEach(function (key) {
        var device = byKey[key];
        if (device.url && !samePage(device.url, select.dataset.currentUrl)) elsewhere += 1;
      });
      if (count === 0 || elsewhere === 0) return;

      /* Which phone the shopper is on: the one this product is for. A
         product cut for two — a lens protector, say — settles on the one
         they have already told the site about, and otherwise on the
         first. A product the line carries that is for no phone in
         particular has none to settle on, so the dropdown opens on a
         placeholder rather than on somebody else's phone. */
      var mine = devices.filter(function (device) {
        return samePage(device.url, select.dataset.currentUrl);
      });
      var remembered = window.peelDeviceSelection ? window.peelDeviceSelection.get() : null;
      var current = null;
      mine.forEach(function (device) {
        if (!current || (remembered && remembered.key === device.key)) current = device;
      });

      if (current) {
        select.value = current.key;
      } else {
        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = select.dataset.placeholder || 'Choose your phone';
        select.insertBefore(placeholder, select.firstChild);
        select.value = '';
      }

      select.addEventListener('change', function () {
        var device = byKey[select.value];
        if (!device || !device.url) return;

        /* One shopper, one phone: chosen here it is chosen in the
           collection filter and the pickers too, the same way the case
           picker remembers it. */
        if (window.peelDeviceSelection) {
          window.peelDeviceSelection.set({
            brand: device.brand,
            model: device.label || device.value || device.key,
            key: device.key
          });
        }

        /* The phone this page is already for — two phones sharing one
           product. Remembered above, but there is nowhere to go. */
        if (samePage(device.url, select.dataset.currentUrl)) return;

        window.location.href = deviceHref(device, currentColorway(section));
      });

      box.hidden = false;
    });
  }

  /* ---- Colorway (variant) selection ---- */
  /* ---- Variant selection ----
     The buy box can carry a swatch row plus a select per remaining option
     (model, size). Read every control, then find the variant matching all
     of them, rather than trusting a variant id baked onto one swatch. */
  function collectSelection(section) {
    var selection = {};
    var checked = section.querySelector('.peel-pdp-swatch-btn input:checked');
    if (checked && checked.dataset.optionPosition) {
      selection[checked.dataset.optionPosition] = checked.value;
    }
    Array.prototype.forEach.call(section.querySelectorAll('[data-peel-pdp-option]'), function (select) {
      selection[select.dataset.optionPosition] = select.value;
    });
    return selection;
  }

  function findVariant(data, selection) {
    var positions = Object.keys(selection);
    return data.variants.filter(function (v) {
      return positions.every(function (pos) { return v['option' + pos] === selection[pos]; });
    })[0];
  }

  /* ---- Gallery follows the colourway ----
     Picking a colourway re-deals the gallery to that colour's own media
     rather than scrolling to a match inside all of it. Nothing else on
     the page moves, so the buy column stays exactly where the shopper
     left it, and the images the other colourways would have contributed
     are still unfetched. */
  function syncGalleryToColor(section, colorName) {
    if (!colorName) return;
    var gallery = section.querySelector('.peel-pdp-gallery');
    if (!gallery) return;

    gallery.setAttribute('data-peel-pdp-color', colorName);
    if (gallery.querySelector('[data-peel-pdp-stack]')) {
      renderStack(gallery, dealStack(gallery));
    } else {
      renderCarousel(gallery);
    }
  }

  function applyVariant(section, data, variant) {
    var priceEl = section.querySelector('[data-peel-pdp-price]');
    var comparePriceEl = section.querySelector('[data-peel-pdp-compare-price]');
    var saveEl = section.querySelector('[data-peel-pdp-save]');
    var addPriceEl = section.querySelector('[data-peel-pdp-add-price]');
    var nameEl = section.querySelector('[data-peel-pdp-colorway-name]');
    var colorTagEl = section.querySelector('[data-peel-pdp-colorway-tag]');
    var addBtn = section.querySelector('[data-peel-pdp-add-to-cart]');
    var variantInput = section.querySelector('[data-peel-pdp-variant-input]');
    var dynamicCheckout = section.querySelector('[data-peel-pdp-dynamic-checkout]');
    var stockEl = section.querySelector('[data-peel-pdp-stock]');

    // What this shopper is shown for the variant: the list price, or the
    // catalog price when they are signed in and tagged for it. The saving
    // is measured from that, so compare_at_price stays the list price.
    var shownPrice = window.Shopify ? Shopify.catalogPrice(variant.price) : variant.price;

    if (priceEl && window.Shopify) priceEl.textContent = Shopify.formatMoney(shownPrice);
    if (addPriceEl && window.Shopify) addPriceEl.textContent = Shopify.formatMoney(shownPrice);
    var onSale = variant.compare_at_price > shownPrice && window.Shopify;
    if (comparePriceEl) {
      if (onSale) {
        comparePriceEl.textContent = Shopify.formatMoney(variant.compare_at_price);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }
    if (saveEl) {
      if (onSale) {
        saveEl.textContent = 'Save ' + Shopify.formatMoney(variant.compare_at_price - shownPrice);
        saveEl.style.display = '';
      } else {
        saveEl.style.display = 'none';
      }
    }

    var colorName = data.colorOptionPosition
      ? variant['option' + data.colorOptionPosition]
      : variant.title;
    if (nameEl) nameEl.textContent = colorName || variant.title;

    /* The launch phrase beside the colour name, shown only while a
       highlighted colourway is the chosen one. The list of them rides on
       the element itself, written by the section from the same theme
       setting the swatch rings read, so there is one answer to which
       colours are highlighted rather than a copy of it here. Matched the
       way the section matches it: case insensitive, whole name, spacing
       around each comma ignored. */
    if (colorTagEl) {
      var flagged = (colorTagEl.dataset.highlightValues || '').split(',');
      var colorKey = (colorName || variant.title || '').toLowerCase().trim();
      var isFlagged = false;
      for (var f = 0; f < flagged.length; f++) {
        var flaggedKey = flagged[f].trim();
        if (flaggedKey && flaggedKey === colorKey) isFlagged = true;
      }
      colorTagEl.hidden = !isFlagged;
    }

    if (addBtn) {
      addBtn.dataset.variantId = variant.id;
      addBtn.disabled = !variant.available;
      btnLabel(addBtn).textContent = variant.available
        ? (addBtn.dataset.label || 'Add to cart')
        : 'Sold out';
      if (addPriceEl) addPriceEl.style.display = variant.available ? '' : 'none';
    }
    if (variantInput) variantInput.value = variant.id;
    if (dynamicCheckout) dynamicCheckout.style.display = variant.available ? '' : 'none';

    syncGalleryToColor(section, colorName);

    if (stockEl) {
      var threshold = parseInt(stockEl.dataset.lowStock, 10) || 0;
      var qty = variant.inventory_quantity;
      var tracked = variant.inventory_management;
      stockEl.textContent = (threshold > 0 && tracked && qty > 0 && qty <= threshold)
        ? qty + ' left in ' + (colorName || variant.title)
        : '';
    }
  }

  window.peelSelectOption = function (el, sectionId) {
    var data = (window.peelPdpData || {})[sectionId];
    var section = document.querySelector('[data-peel-pdp-section-id="' + sectionId + '"]');
    if (!data || !section) return;
    var variant = findVariant(data, collectSelection(section));
    if (variant) applyVariant(section, data, variant);
  };

  window.peelSelectColorway = function (radio, sectionId) {
    var row = radio.closest('.peel-pdp-swatch-row');
    if (row) {
      Array.prototype.forEach.call(row.querySelectorAll('.peel-pdp-swatch-btn'), function (l) {
        l.classList.remove('is-selected');
      });
      radio.closest('.peel-pdp-swatch-btn').classList.add('is-selected');
    }
    window.peelSelectOption(radio, sectionId);
  };

  /* ---- Add to cart: main variant + any checked addons ----
     The button's text lives in its own span, with the price in a second
     one beside it, so every transient state writes to the label rather
     than to the button — writing the button would delete both spans. */
  function btnLabel(btn) {
    return btn.querySelector('[data-peel-pdp-add-label]') || btn;
  }

  /* The chosen variant at the quantity in the stepper, plus a unit of each
     ticked add-on. Read off the form rather than off the button that was
     clicked, so the accelerated buttons below can buy the same basket. */
  function collectItems(section) {
    var qtyInput = section.querySelector('[data-peel-pdp-qty]');
    var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    var variantInput = section.querySelector('[data-peel-pdp-variant-input]');
    var addBtn = section.querySelector('[data-peel-pdp-add-to-cart]');
    var variantId = (variantInput && variantInput.value) || (addBtn && addBtn.dataset.variantId);
    var items = [{ id: parseInt(variantId, 10), quantity: quantity }];

    /* The line records that it came from here. The cart reads it back to
       decide whether the add-on discount still has anything to discount,
       and the leading underscore keeps it out of the line's customer-facing
       text in the cart, the checkout and the order. */
    section.querySelectorAll('[data-peel-pdp-addon]:checked').forEach(function (addon) {
      items.push({
        id: parseInt(addon.dataset.variantId, 10),
        quantity: 1,
        properties: { _addon_source: 'pdp' }
      });
    });
    return items;
  }

  /* The codes the ticked add-ons ask for, deduped. Two add-ons matched by
     the same rule name the same code, and the cart wants it once.

     A card that names no code of its own falls back to the store-wide one
     from Theme settings > Add-on offers. That is the setting's stated
     meaning — the base every add-on gets unless a rule says otherwise —
     and reading it here rather than only in the section keeps it true of
     markup the section did not write: an add-on rendered by another
     template, or by a copy of this one that predates data-addon-code. */
  function collectAddonCodes(section) {
    var codes = [];
    var base = (window.peelAddonDiscountBaseCode || '').trim();
    section.querySelectorAll('[data-peel-pdp-addon]:checked').forEach(function (addon) {
      var code = (addon.dataset.addonCode || '').trim() || base;
      if (code && codes.indexOf(code) === -1) codes.push(code);
    });
    return codes;
  }

  function initAddToCart() {
    document.querySelectorAll('[data-peel-pdp-add-to-cart]').forEach(function (btn) {
      /* Only as a fallback: the markup sets data-label, and reading it
         back off the button would pick up the price span's text too. */
      if (!btn.dataset.label) btn.dataset.label = btnLabel(btn).textContent.trim();
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var section = btn.closest('.peel-pdp-main-section');
        if (!section) return;

        var items = collectItems(section);

        btn.disabled = true;
        btnLabel(btn).textContent = 'Adding…';

        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items, sections: ['cart-drawer'] })
        })
          .then(function (response) { return response.json(); })
          /* An add-on went in, so the discount that pays for the price on
             its card goes on the cart. Its response carries the drawer
             rendered after the discount rather than before it, so the
             drawer opens showing the total that will actually be charged
             — and the first item is the product itself, so anything past
             it is an add-on. */
          .then(function (cart) {
            var codes = collectAddonCodes(section);
            if (!codes.length || typeof applyAddonDiscount !== 'function') return cart;
            return applyAddonDiscount(codes, ['cart-drawer']).then(function (discounted) {
              return discounted || cart;
            });
          })
          .then(function (cart) {
            if (document.getElementById('shopify-section-cart-drawer')) {
              document.getElementById('shopify-section-cart-drawer').outerHTML = cart.sections['cart-drawer'];
            }
            cartDrawer.showModal();
            _updateCartItemCount(cart.item_count);
            btnLabel(btn).textContent = 'Added ✓';
            setTimeout(function () {
              btnLabel(btn).textContent = btn.dataset.label;
              btn.disabled = false;
            }, 1400);
          })
          .catch(function () {
            btnLabel(btn).textContent = btn.dataset.label;
            btn.disabled = false;
          });
      });
    });
  }

  /* ---- Accelerated checkout with add-ons ----
     Shopify's wallet buttons check out the one variant in the product form:
     they ignore the cart, and they ignore anything ticked beside them, so a
     shopper who ticked an add-on and paid with "Buy with Shop" reached the
     Shop screen holding the case alone. Nothing on the page can widen what
     those buttons buy — the click happens inside Shopify's own iframe — so
     while an add-on is ticked they step aside for a Shop Pay button of our
     own, which carries every ticked item into the Shop checkout as a cart
     permalink. Tick nothing and the native buttons are back, untouched. */
  /* ---- The rating row jumps to the reviews ----
     The stars and the review count sit at the top of the buybox and the
     Judge.me widget sits near the foot of the page, so the row is an
     anchor to it. The scroll itself is the browser's — html carries
     scroll-behavior: smooth — and all this does is catch the one case
     that would misbehave: a product template that carries no review
     widget, where an unmatched fragment sends the page to the top
     instead. Resolved on the click rather than up front, because the
     widget can be drawn late. */
  function initReviewsLink() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('[data-peel-reviews-link]') : null;
      if (!link || !link.hasAttribute('href')) return;
      if (!document.getElementById('judgeme_product_reviews')) event.preventDefault();
    });
  }

  function initDynamicCheckout() {
    document.querySelectorAll('[data-peel-pdp-addon-checkout]').forEach(function (btn) {
      var section = btn.closest('.peel-pdp-main-section');
      var container = btn.parentNode;
      if (!section || !container) return;

      function sync() {
        var hasAddons = section.querySelectorAll('[data-peel-pdp-addon]:checked').length > 0;
        btn.hidden = !hasAddons;
        /* The class, not the button, hides Shopify's row: the wallet buttons
           are rendered once and kept, so unticking brings them straight back
           rather than waiting on Shopify to draw them again. */
        container.classList.toggle('has-addons', hasAddons);
      }

      section.addEventListener('change', function (event) {
        if (event.target.matches('[data-peel-pdp-addon]')) sync();
      });
      sync();

      btn.addEventListener('click', function () {
        var pairs = collectItems(section)
          .filter(function (item) { return item.id > 0; })
          .map(function (item) { return item.id + ':' + item.quantity; });
        if (!pairs.length) return;
        /* /cart/<variant>:<qty>,… lands on the checkout holding exactly those
           items, and payment=shop_pay opens it in Shop — the button the
           shopper pressed. This path never touches the cart, so the add-on
           discount cannot be applied to it the way the Add to cart button
           applies it; the permalink carries the code itself instead. */
        var addonCodes = collectAddonCodes(section);
        var permalink = window.Shopify.routes.root + 'cart/' + pairs.join(',') + '?payment=shop_pay';
        if (addonCodes.length) {
          permalink += '&discount=' + encodeURIComponent(addonCodes.join(','));
        }
        window.location.href = permalink;
      });
    });
  }
})();
